/**
 * Outil « Courbe paramétrée » — logique pure (MPSI/PTSI → PT).
 *
 * Notations des cartes « Étude d'une courbe paramétrée », « Points singuliers
 * et tangente », « Repère de Frenet », « Courbure d'une courbe plane »,
 * « Abscisse curviligne et longueur d'un arc » : f(t) = (x(t), y(t)),
 * f′(t) dirige la tangente en un point régulier ; en un point singulier, p est
 * le premier ordre avec f⁽ᵖ⁾(t₀) ≠ 0 (il dirige la tangente) et q > p le
 * premier ordre non colinéaire (allure selon les parités de p et q) ;
 * γ = (x′y″ − x″y′)/(x′² + y′²)^(3/2), R = 1/|γ| ; L = ∫ ‖f′(t)‖ dt.
 *
 * Les dérivées successives en t₀ viennent d'un développement de Taylor
 * calculé en arithmétique de séries tronquées (« jets ») : exactes au
 * flottant près, à tout ordre, sans dériver à la main.
 */

/* ---------- jets : séries de Taylor tronquées en t₀ ---------- */

/** coefficients c₀ … c_{K−1} de u(t₀ + h) = Σ c_k h^k */
export type Jet = number[];

export const K = 7;
export const cst = (c: number, k = K): Jet => [c, ...Array<number>(k - 1).fill(0)];
export const variable = (t0: number, k = K): Jet => [t0, 1, ...Array<number>(k - 2).fill(0)];
export const jadd = (a: Jet, b: Jet): Jet => a.map((v, i) => v + b[i]!);
export const jsub = (a: Jet, b: Jet): Jet => a.map((v, i) => v - b[i]!);
export const jscale = (a: Jet, s: number): Jet => a.map((v) => v * s);
export function jmul(a: Jet, b: Jet): Jet {
  return a.map((_, k) => {
    let s = 0;
    for (let j = 0; j <= k; j++) s += a[j]! * b[k - j]!;
    return s;
  });
}
export function jpow(a: Jet, n: number): Jet {
  let r = cst(1, a.length);
  for (let i = 0; i < n; i++) r = jmul(r, a);
  return r;
}
/** sin u et cos u ensemble (s′ = c u′, c′ = −s u′) */
export function jsincos(u: Jet): [Jet, Jet] {
  const n = u.length;
  const s = [Math.sin(u[0]!)];
  const c = [Math.cos(u[0]!)];
  for (let k = 1; k < n; k++) {
    let ss = 0;
    let cc = 0;
    for (let j = 1; j <= k; j++) {
      ss += j * u[j]! * c[k - j]!;
      cc -= j * u[j]! * s[k - j]!;
    }
    s.push(ss / k);
    c.push(cc / k);
  }
  return [s, c];
}
export const jsin = (u: Jet) => jsincos(u)[0];
export const jcos = (u: Jet) => jsincos(u)[1];

/** dérivées u(t₀), u′(t₀), u″(t₀)… : k! c_k */
export function derivatives(j: Jet): number[] {
  let f = 1;
  return j.map((c, k) => {
    if (k > 0) f *= k;
    return c * f;
  });
}

/* ---------- courbes proposées ---------- */

type Coord = (t: Jet) => Jet;

export interface Singular {
  t: number;
  /** « 0 », « 2π », « π/2 » */
  label: string;
}

export interface Preset {
  id: string;
  /** « x = cos t, y = sin 2t » */
  label: string;
  x: Coord;
  y: Coord;
  /** intervalle tracé */
  a: number;
  b: number;
  /** paramètre en multiples de π/24 (sinon décimal au pas de 0,05) */
  trig: boolean;
  singular: Singular[];
  /** longueur exacte de [a ; b], si elle se calcule */
  length?: { value: number; text: string };
  note: string;
}

const PI = Math.PI;
const t2 = (t: Jet) => jmul(t, t);

export const PRESETS: Preset[] = [
  {
    id: 'lissajous',
    label: 'x = cos t, y = sin 2t',
    x: (t) => jcos(t),
    y: (t) => jsin(jscale(t, 2)),
    a: -PI,
    b: PI,
    trig: true,
    singular: [],
    note: 'Période 2π. t → −t donne (x ; −y) : symétrie par rapport à (Ox) ; t → π − t donne (−x ; −y) : symétrie de centre O. On étudie sur [0 ; π/2] puis on complète par symétries. Tangente horizontale si y′ = 0, verticale si x′ = 0.',
  },
  {
    id: 'cusp',
    label: 'x = t², y = t³',
    x: (t) => t2(t),
    y: (t) => jpow(t, 3),
    a: -1.5,
    b: 1.5,
    trig: false,
    singular: [{ t: 0, label: '0' }],
    note: 'En t = 0, f′(0) = (0 ; 0) : point singulier. f″(0) = (2 ; 0) dirige la tangente (horizontale) ; f‴(0) = (0 ; 6) n’est pas colinéaire : p = 2, q = 3, point de rebroussement.',
  },
  {
    id: 'cycloid',
    label: 'x = t − sin t, y = 1 − cos t',
    x: (t) => jsub(t, jsin(t)),
    y: (t) => jsub(cst(1, t.length), jcos(t)),
    a: 0,
    b: 4 * PI,
    trig: true,
    singular: [
      { t: 0, label: '0' },
      { t: 2 * PI, label: '2π' },
      { t: 4 * PI, label: '4π' },
    ],
    length: { value: 16, text: '16, soit 8 par arche' },
    note: 'La cycloïde : un point d’un cercle de rayon 1 qui roule sur (Ox). En t = 2kπ, f′ = (0 ; 0) : points de rebroussement, tangente verticale. Une arche mesure 8.',
  },
  {
    id: 'circle',
    label: 'x = 2 cos t, y = 2 sin t',
    x: (t) => jscale(jcos(t), 2),
    y: (t) => jscale(jsin(t), 2),
    a: 0,
    b: 2 * PI,
    trig: true,
    singular: [],
    length: { value: 4 * PI, text: '4π' },
    note: '‖f′(t)‖ = 2 sur [0 ; 2π] : L = 2 × 2π = 4π. La courbure vaut 1/2 partout : le cercle osculateur est le cercle lui-même, N pointe vers le centre.',
  },
  {
    id: 'astroid',
    label: 'x = cos³ t, y = sin³ t',
    x: (t) => jpow(jcos(t), 3),
    y: (t) => jpow(jsin(t), 3),
    a: 0,
    b: 2 * PI,
    trig: true,
    singular: [
      { t: 0, label: '0' },
      { t: PI / 2, label: 'π/2' },
      { t: PI, label: 'π' },
      { t: (3 * PI) / 2, label: '3π/2' },
    ],
    length: { value: 6, text: '6' },
    note: 'L’astroïde : f′(t) = 3 sin t cos t (−cos t ; sin t) s’annule en t = kπ/2 : quatre points de rebroussement. ‖f′(t)‖ = 3|sin t cos t| : L = 6.',
  },
  {
    id: 'cardioid',
    label: 'x = (1 + cos t) cos t, y = (1 + cos t) sin t',
    x: (t) => {
      const c = jcos(t);
      return jmul(jadd(cst(1, t.length), c), c);
    },
    y: (t) => {
      const [s, c] = jsincos(t);
      return jmul(jadd(cst(1, t.length), c), s);
    },
    a: 0,
    b: 2 * PI,
    trig: true,
    singular: [{ t: PI, label: 'π' }],
    length: { value: 8, text: '8' },
    note: 'La cardioïde (ρ = 1 + cos θ en polaire) : f′(π) = (0 ; 0), point de rebroussement à l’origine. ‖f′(t)‖ = 2|cos(t/2)| : L = 8.',
  },
  {
    id: 'parabola',
    label: 'x = t, y = t²',
    x: (t) => t,
    y: (t) => t2(t),
    a: -2,
    b: 2,
    trig: false,
    singular: [],
    note: 'Le graphe de y = x² : γ = y″/(1 + y′²)^(3/2) = 2/(1 + 4t²)^(3/2) (dérivées par rapport à x), qui vaut 2 au sommet, R = 1/2 ; la courbure diminue quand on s’éloigne du sommet.',
  },
  {
    id: 'beak',
    label: 'x = t², y = t⁴ + t⁵',
    x: (t) => t2(t),
    y: (t) => jadd(jpow(t, 4), jpow(t, 5)),
    a: -1.2,
    b: 1,
    trig: false,
    singular: [{ t: 0, label: '0' }],
    note: 'En t = 0 : f″(0) = (2 ; 0), f‴(0) = (0 ; 0), f⁽⁴⁾(0) = (0 ; 24) : p = 2, q = 4, tous deux pairs — rebroussement de seconde espèce (en « bec »).',
  },
  {
    id: 'inflex',
    label: 'x = t³, y = t⁵',
    x: (t) => jpow(t, 3),
    y: (t) => jpow(t, 5),
    a: -1.2,
    b: 1.2,
    trig: false,
    singular: [{ t: 0, label: '0' }],
    note: 'En t = 0 : f′(0) = f″(0) = 0, f‴(0) = (6 ; 0) : p = 3 ; f⁽⁵⁾(0) = (0 ; 120) : q = 5. p et q impairs : point d’inflexion (singulier), la courbe traverse sa tangente.',
  },
];

export const presetById = (id: string) => PRESETS.find((p) => p.id === id) ?? PRESETS[0]!;

/* ---------- valeurs en un point ---------- */

/** dérivées de x et y en t₀ jusqu'à l'ordre K − 1 */
export function jets(p: Preset, t0: number) {
  const t = variable(t0);
  return { x: derivatives(p.x(t)), y: derivatives(p.y(t)) };
}

export const point = (p: Preset, t0: number) => {
  const t = [t0];
  return { x: p.x(t)[0]!, y: p.y(t)[0]! };
};

/** ‖f′(t)‖ (série d'ordre 1) */
export function speed(p: Preset, t0: number) {
  const t = variable(t0, 2);
  return Math.hypot(p.x(t)[1]!, p.y(t)[1]!);
}

const EPS = 1e-9;

export interface Local {
  x: number;
  y: number;
  dx: number;
  dy: number;
  ddx: number;
  ddy: number;
  regular: boolean;
  /** courbure signée (point régulier) */
  gamma: number | null;
  /** tangent unitaire, normal directement orthogonal */
  T: { x: number; y: number } | null;
  N: { x: number; y: number } | null;
}

export function local(p: Preset, t0: number): Local {
  const { x, y } = jets(p, t0);
  const [X, dx, ddx] = x;
  const [Y, dy, ddy] = y;
  const v = Math.hypot(dx!, dy!);
  const regular = v > 1e-7;
  const T = regular ? { x: dx! / v, y: dy! / v } : null;
  return {
    x: X!,
    y: Y!,
    dx: dx!,
    dy: dy!,
    ddx: ddx!,
    ddy: ddy!,
    regular,
    gamma: regular ? (dx! * ddy! - ddx! * dy!) / v ** 3 : null,
    T,
    N: T ? { x: -T.y, y: T.x } : null,
  };
}

/* ---------- points singuliers : p, q, allure ---------- */

export type Shape = 'ordinaire' | 'inflexion' | 'rebroussement1' | 'rebroussement2';

export const SHAPE_TEXT: Record<Shape, string> = {
  ordinaire: 'point ordinaire',
  inflexion: 'point d’inflexion',
  rebroussement1: 'point de rebroussement de première espèce',
  rebroussement2: 'point de rebroussement de seconde espèce',
};

export function shapeOf(p: number, q: number): Shape {
  if (p % 2 === 1) return q % 2 === 0 ? 'ordinaire' : 'inflexion';
  return q % 2 === 1 ? 'rebroussement1' : 'rebroussement2';
}

export interface SingularInfo {
  /** f⁽ᵏ⁾(t₀) pour k = 1 … K − 1 */
  ders: { x: number; y: number }[];
  p: number;
  q: number;
  shape: Shape;
}

export function singularInfo(pr: Preset, t0: number): SingularInfo | null {
  const { x, y } = jets(pr, t0);
  const ders = x.slice(1).map((vx, i) => ({ x: clean(vx), y: clean(y[i + 1]!) }));
  const pi = ders.findIndex((d) => Math.hypot(d.x, d.y) > EPS);
  if (pi < 0) return null;
  const P = ders[pi]!;
  const qi = ders.findIndex(
    (d, k) => k > pi && Math.abs(P.x * d.y - P.y * d.x) > EPS * Math.max(1, Math.hypot(d.x, d.y)),
  );
  if (qi < 0) return null;
  return { ders, p: pi + 1, q: qi + 1, shape: shapeOf(pi + 1, qi + 1) };
}

/** arrondit les résidus de calcul flottant (1e−15 → 0, 5,999999999 → 6) */
export function clean(v: number) {
  const r = Math.round(v);
  if (Math.abs(v - r) < 1e-9) return r === 0 ? 0 : r;
  return v;
}

/* ---------- longueur ---------- */

/** ∫ₐᵇ ‖f′(t)‖ dt (Simpson composite) */
export function arcLength(p: Preset, a: number, b: number, n = 600): number {
  if (b === a) return 0;
  const m = n % 2 ? n + 1 : n;
  const h = (b - a) / m;
  let s = speed(p, a) + speed(p, b);
  for (let i = 1; i < m; i++) s += (i % 2 ? 4 : 2) * speed(p, a + i * h);
  return (s * h) / 3;
}

/* ---------- écriture ---------- */

/** « 7π/12 », « −π/2 », « 2π », « 0 » : k π/24 réduit */
export function piText(k: number): string {
  if (k === 0) return '0';
  const g = gcd(Math.abs(k), 24);
  const num = k / g;
  const den = 24 / g;
  const n = Math.abs(num) === 1 ? 'π' : `${Math.abs(num)}π`;
  return `${num < 0 ? '−' : ''}${n}${den === 1 ? '' : `/${den}`}`;
}
function gcd(a: number, b: number): number {
  while (b) [a, b] = [b, a % b];
  return a;
}

/** « 1,25 », « −0,333 » : 3 décimales au plus, signe − */
export function dec(v: number, d = 3): string {
  const r = Number(clean(v).toFixed(d));
  return String(Object.is(r, -0) ? 0 : r)
    .replace('.', ',')
    .replace('-', '−');
}

const SUPS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
/** « f′ », « f″ », « f‴ », « f⁽⁴⁾ » */
export function fDer(k: number): string {
  if (k === 1) return 'f′';
  if (k === 2) return 'f″';
  if (k === 3) return 'f‴';
  return `f⁽${String(k)
    .split('')
    .map((c) => SUPS[Number(c)])
    .join('')}⁾`;
}

/* ---------- défi ---------- */

export type QuizKind = 'tangent' | 'singular' | 'shape' | 'curvature' | 'length';
export const QUIZ_KINDS: QuizKind[] = ['tangent', 'singular', 'shape', 'curvature', 'length'];

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}
export interface Quiz {
  kind: QuizKind;
  prompt: string;
  options: QuizOption[];
  explain: string;
}

type Rng = () => number;
const int = (lo: number, hi: number, rng: Rng) => lo + Math.floor(rng() * (hi - lo + 1));
const pick = <T>(xs: readonly T[], rng: Rng): T => xs[Math.floor(rng() * xs.length)]!;
function shuffle<T>(a: readonly T[], rng: Rng): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}
/** la bonne réponse d'abord ; `ok` écarte un distracteur (vecteur colinéaire…) */
function finish(
  kind: QuizKind,
  prompt: string,
  cands: QuizOption[],
  explain: string,
  rng: Rng,
  ok?: (u: number, v: number) => boolean,
) {
  const seen = new Set<string>();
  const kept = cands.filter((c, i) => {
    if (seen.has(c.text)) return false;
    seen.add(c.text);
    if (i === 0 || !ok) return true;
    const v = /^\((−?[\d.]+) ; (−?[\d.]+)\)$/.exec(c.text);
    if (!v) return true;
    const n = (t: string) => Number(t.replace('−', '-'));
    return ok(n(v[1]!), n(v[2]!));
  });
  if (kept.length < 4) return null;
  const [good, ...rest] = kept;
  return {
    kind,
    prompt,
    options: shuffle([good!, ...shuffle(rest, rng)].slice(0, 4), rng),
    explain,
  };
}
const m = (v: number) => String(v).replace('-', '−');
const vec = (a: number, b: number) => `(${m(a)} ; ${m(b)})`;
/** « t² − 2t », « t³ − 12t » */
const polyT = (deg: number, c: number) => (c === 0 ? `t${SUPS[deg]}` : `t${SUPS[deg]} − ${m(c)}t`);

/** x = t² − 2at, y = t³ − 3a²t : f′ = (2t − 2a ; 3t² − 3a²) */
function quizTangent(rng: Rng): Quiz | null {
  const a = int(1, 2, rng);
  const t0 = pick(
    [-2, -1, 0, 1, 2, 3].filter((v) => v !== a),
    rng,
  );
  const X = t0 * t0 - 2 * a * t0;
  const Y = t0 ** 3 - 3 * a * a * t0;
  const dx = 2 * t0 - 2 * a;
  const dy = 3 * t0 * t0 - 3 * a * a;
  const ddx = 2;
  const ddy = 6 * t0;
  return finish(
    'tangent',
    `x(t) = ${polyT(2, 2 * a)}, y(t) = ${polyT(3, 3 * a * a)}. Un vecteur directeur de la tangente au point de paramètre t = ${m(t0)} ?`,
    [
      { text: vec(dx, dy), correct: true },
      {
        text: vec(X, Y),
        correct: false,
        hint: `C’est le point f(${m(t0)}) lui-même, pas un vecteur tangent.`,
      },
      {
        text: vec(dy, dx),
        correct: false,
        hint: 'Composantes échangées : f′(t) = (x′(t) ; y′(t)).',
      },
      {
        text: vec(ddx, ddy),
        correct: false,
        hint: `C’est f″(${m(t0)}) ; il ne dirige la tangente que si f′(${m(t0)}) = 0 (point singulier).`,
      },
      { text: vec(dx, -dy), correct: false, hint: 'Signe de y′ à revoir.' },
    ],
    `x′(t) = 2t − ${2 * a}, y′(t) = 3t² − ${3 * a * a} ; en t = ${m(t0)}, f′ = ${vec(dx, dy)} ≠ 0 : le point est régulier et f′(${m(t0)}) dirige la tangente.`,
    rng,
    // un vecteur colinéaire à f′(t₀) dirigerait aussi la tangente : écarté
    (u, v) => u * dy - v * dx !== 0,
  );
}

function quizSingular(rng: Rng): Quiz | null {
  const a = int(1, 3, rng);
  const sing = rng() < 0.5 ? a : -a;
  // x = t² − 2·sing·t s'annule en dérivée en t = sing seulement ; y = t³ − 3a²t en ±a
  const c = 2 * sing;
  const opts = [sing, -sing, 0, 2 * sing];
  const der = (t: number) => vec(2 * t - c, 3 * t * t - 3 * a * a);
  return finish(
    'singular',
    `x(t) = t² ${c > 0 ? '−' : '+'} ${Math.abs(c)}t, y(t) = ${polyT(3, 3 * a * a)}. Pour quelle valeur de t le point est-il singulier ?`,
    opts.map((t) => ({
      text: `t = ${m(t)}`,
      correct: t === sing,
      hint:
        t === sing
          ? undefined
          : `En t = ${m(t)}, f′ = ${der(t)} ≠ 0 : point régulier${3 * t * t - 3 * a * a === 0 ? ' (y′ = 0 seulement : tangente horizontale)' : ''}.`,
    })),
    `Singulier ⟺ x′(t) = 0 ET y′(t) = 0 : x′(t) = 2t ${c > 0 ? '−' : '+'} ${Math.abs(c)} = 0 donne t = ${m(sing)}, et y′(${m(sing)}) = 3·${sing < 0 ? `(${m(sing)})` : sing}² − ${3 * a * a} = 0.`,
    rng,
  );
}

const SHAPES: Shape[] = ['ordinaire', 'inflexion', 'rebroussement1', 'rebroussement2'];
const SHAPE_HINT: Record<Shape, string> = {
  ordinaire:
    'p impair, q pair : la courbe reste d’un côté de sa tangente sans rebrousser, comme en un point régulier « ordinaire ».',
  inflexion: 'p et q impairs : la courbe traverse sa tangente (inflexion).',
  rebroussement1:
    'p pair, q impair : la courbe repart en arrière, de part et d’autre de la tangente.',
  rebroussement2: 'p et q pairs : la courbe repart en arrière, du même côté de la tangente (bec).',
};

interface ShapeCase {
  curve: string;
  p: number;
  q: number;
  /** dérivées d’ordre p et q en 0 */
  P: string;
  Q: string;
}
const SHAPE_CASES: ShapeCase[] = [
  { curve: 'x = t², y = t³', p: 2, q: 3, P: '(2 ; 0)', Q: '(0 ; 6)' },
  { curve: 'x = t², y = t⁵', p: 2, q: 5, P: '(2 ; 0)', Q: '(0 ; 120)' },
  { curve: 'x = t³, y = t⁴', p: 3, q: 4, P: '(6 ; 0)', Q: '(0 ; 24)' },
  { curve: 'x = t³, y = t⁵', p: 3, q: 5, P: '(6 ; 0)', Q: '(0 ; 120)' },
  { curve: 'x = t, y = t³', p: 1, q: 3, P: '(1 ; 0)', Q: '(0 ; 6)' },
  { curve: 'x = t, y = t²', p: 1, q: 2, P: '(1 ; 0)', Q: '(0 ; 2)' },
  // x = t², y = t⁴ « repasserait sur elle-même » (f(−t) = f(t)) : le bec a besoin de t⁵
  { curve: 'x = t², y = t⁴ + t⁵', p: 2, q: 4, P: '(2 ; 0)', Q: '(0 ; 24)' },
];

function quizShape(rng: Rng): Quiz | null {
  const k = pick(SHAPE_CASES, rng);
  const good = shapeOf(k.p, k.q);
  return finish(
    'shape',
    `${k.curve} en t = 0 : quelle est l’allure de la courbe ?`,
    [good, ...SHAPES.filter((s) => s !== good)].map((s) => ({
      text: SHAPE_TEXT[s].replace(/^point/, 'Point'),
      correct: s === good,
      hint: s === good ? undefined : `Ici p = ${k.p} et q = ${k.q}. ${SHAPE_HINT[good]}`,
    })),
    `${fDer(k.p)}(0) = ${k.P} est le premier vecteur dérivé non nul : p = ${k.p}, il dirige la tangente (horizontale). ${fDer(k.q)}(0) = ${k.Q} est le premier non colinéaire : q = ${k.q}. ${SHAPE_HINT[good]}`,
    rng,
  );
}

function quizCurvature(rng: Rng): Quiz | null {
  if (rng() < 0.5) {
    const R = pick([2, 3, 4, 5], rng);
    return finish(
      'curvature',
      `Quelle est la courbure du cercle x = ${R} cos t, y = ${R} sin t (parcouru dans le sens direct) ?`,
      [
        { text: `1/${R}`, correct: true },
        { text: `${R}`, correct: false, hint: `C’est le rayon de courbure R = 1/|γ| = ${R}.` },
        {
          text: `1/${R * R}`,
          correct: false,
          hint: 'Pas de carré : γ = 1/R pour un cercle de rayon R.',
        },
        { text: '0', correct: false, hint: 'Courbure nulle : c’est une droite, pas un cercle.' },
        {
          text: `−1/${R}`,
          correct: false,
          hint: 'Parcouru dans le sens direct, le cercle tourne vers la gauche : γ > 0.',
        },
        {
          text: `${2 * R}`,
          correct: false,
          hint: 'Le diamètre ne donne pas la courbure : γ = 1/R.',
        },
      ],
      `x′ = −${R} sin t, y′ = ${R} cos t, x″ = −${R} cos t, y″ = −${R} sin t : x′y″ − x″y′ = ${R * R} et (x′² + y′²)^(3/2) = ${R ** 3}, donc γ = ${R * R}/${R ** 3} = 1/${R}.`,
      rng,
    );
  }
  const a = pick([1, 2, 3, 5], rng);
  return finish(
    'curvature',
    `Quelle est la courbure du graphe de y = ${a === 1 ? '' : a}x² au sommet (0 ; 0) ?`,
    [
      { text: `${2 * a}`, correct: true },
      {
        text: `${a}`,
        correct: false,
        hint: `γ = y″(0)/(1 + y′(0)²)^(3/2) et y″(0) = ${2 * a}, pas ${a}.`,
      },
      { text: `1/${2 * a}`, correct: false, hint: `C’est le rayon de courbure R = 1/γ.` },
      { text: '0', correct: false, hint: 'y″(0) ≠ 0 : la courbure n’est pas nulle au sommet.' },
    ],
    `Graphe de y = g(x) : γ = g″/(1 + g′²)^(3/2) ; ici g′(0) = 0 et g″(0) = ${2 * a}, donc γ = ${2 * a} et R = 1/${2 * a}.`,
    rng,
  );
}

type Dur = 'π' | 'π/2' | '2' | '3';
/** « 3π », « π/2 », « 3π/2 », « 6 » : c × T écrit simplement */
function times(c: number, T: Dur): string {
  if (T === 'π') return c === 1 ? 'π' : `${c}π`;
  if (T === 'π/2') {
    if (c % 2 === 0) return c === 2 ? 'π' : `${c / 2}π`;
    return c === 1 ? 'π/2' : `${c}π/2`;
  }
  return `${c * Number(T)}`;
}
const piMul = (c: number) => (c === 1 ? 'π' : `${c}π`);
/** « 1 − cos t » ou « 2(1 − cos t) » */
const kf = (k: number, e: string) => (k === 1 ? e : `${k}(${e})`);
const kc = (k: number, e: string) => (k === 1 ? e : `${k} ${e}`);

function quizLength(rng: Rng): Quiz | null {
  const R = pick([1, 2, 3, 4], rng);
  if (rng() < 0.5) {
    const T = pick(['π', 'π/2', '2', '3'] as const, rng);
    const good = times(R, T);
    return finish(
      'length',
      `Longueur de l’arc x = ${kc(R, 'cos t')}, y = ${kc(R, 'sin t')} pour t ∈ [0 ; ${T}] ?`,
      [
        { text: good, correct: true },
        {
          text: times(R * R, T),
          correct: false,
          hint: `‖f′(t)‖ = ${R}, pas ${R * R} : L = ∫ ${R} dt.`,
        },
        {
          text: piMul(2 * R),
          correct: false,
          hint: 'C’est le périmètre du cercle entier (t ∈ [0 ; 2π]).',
        },
        {
          text: T,
          correct: false,
          hint: `‖f′(t)‖ = ${R} : il faut multiplier la longueur de l’intervalle par ${R}.`,
        },
        {
          text: `${R}`,
          correct: false,
          hint: `${R} est la vitesse ‖f′(t)‖ ; la longueur l’intègre sur [0 ; ${T}].`,
        },
      ],
      `x′ = −${kc(R, 'sin t')}, y′ = ${kc(R, 'cos t')} : ‖f′(t)‖ = ${R} (constante), donc L = ${R} × ${T} = ${good}.`,
      rng,
    );
  }
  return finish(
    'length',
    `Longueur d’une arche de cycloïde x = ${kf(R, 't − sin t')}, y = ${kf(R, '1 − cos t')}, t ∈ [0 ; 2π] ?`,
    [
      { text: `${8 * R}`, correct: true },
      {
        text: piMul(2 * R),
        correct: false,
        hint: 'C’est le périmètre du cercle qui roule, pas la longueur de l’arche.',
      },
      {
        text: `${4 * R}`,
        correct: false,
        hint: `‖f′(t)‖ = ${2 * R} sin(t/2) sur [0 ; 2π] et l’intégrale de sin(t/2) sur [0 ; 2π] vaut 4 : L = ${8 * R}.`,
      },
      {
        text: piMul(4 * R),
        correct: false,
        hint: `L’intégrale de ${2 * R} sin(t/2) sur [0 ; 2π] vaut ${8 * R}, sans π.`,
      },
    ],
    `x′ = ${kf(R, '1 − cos t')}, y′ = ${kc(R, 'sin t')} : ‖f′(t)‖² = ${kf(R * R, '2 − 2 cos t')} = ${4 * R * R} sin²(t/2), donc ‖f′(t)‖ = ${2 * R} sin(t/2) sur [0 ; 2π] et L = [−${4 * R} cos(t/2)] entre 0 et 2π = ${8 * R}.`,
    rng,
  );
}

const MAKERS: Record<QuizKind, (rng: Rng) => Quiz | null> = {
  tangent: quizTangent,
  singular: quizSingular,
  shape: quizShape,
  curvature: quizCurvature,
  length: quizLength,
};

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  for (let i = 0; i < 500; i++) {
    const q = MAKERS[kind](rng);
    if (q) return q;
  }
  throw new Error(`défi ${kind} introuvable`);
}
